/** @file Shared site membership fields for existing-user and invitation forms. */
import { collection, getDocs } from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../../app/firebase/firebase.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { getNativeFieldWrapperHtml } from "../../shared/forms/nativeFieldStyles.js";

/**
 * Holds ordinary site controls until the Settings activation contract exists.
 * The later owner must require enabled multisite AND more than one active site.
 * Site creation or deployment alone must never expose these fields.
 * @returns {boolean} Whether site assignment controls are released.
 */
export function areUserSiteFieldsAvailable() {
  return false;
}

/**
 * Renders the standard site field placeholders.
 * @returns {string} Field markup.
 */
export function renderUserSiteFields() {
  if (!areUserSiteFieldsAvailable()) {return "";}
  return `<div class="grid grid-cols-1 gap-4 sm:grid-cols-2" data-user-site-fields>
    ${getNativeFieldWrapperHtml("Sites", '<div data-user-sites-control></div>', {required: true})}
    ${getNativeFieldWrapperHtml("Default site", '<div data-user-default-site-control></div>', {required: true})}
    <p class="text-sm text-gray-500 sm:col-span-2 sm:text-xs" data-site-fields-status role="status">Loading site options…</p>
  </div>`;
}

/**
 * Mounts canonical site-ID controls using the registered form-value contract.
 * @param {HTMLFormElement} form Source form.
 * @param {object} source Editing snapshot or initial invitation.
 * @param {string|null} businessDefaultSiteId Existing configured default.
 * @returns {Promise<void>}
 */
export async function setupUserSiteFields(form, source, businessDefaultSiteId) {
  form.dataset.siteFieldsReady = "false";
  const root = form.querySelector("[data-user-site-fields]");
  if (!areUserSiteFieldsAvailable()) {
    root?.remove();
    form.dataset.siteFieldsReady = "true";
    return;
  }
  if (!root) {return;}
  const status = root.querySelector("[data-site-fields-status]");
  try {
    const snapshot = await getDocs(collection(db, "sites"));
    if (!root.isConnected) {return;}
    const sites = snapshot.docs.map((doc) => ({id: doc.id, ...doc.data()}))
      .sort((a, b) => (a.siteName || "").localeCompare(b.siteName || ""));
    const selected = Array.isArray(source.siteIds) ? [...source.siteIds] :
      businessDefaultSiteId ? [businessDefaultSiteId] : [];
    if (!sites.some((site) => site.isActive === true) || selected.some((id) =>
      !sites.some((site) => site.id === id))) {
      throw new Error("Site assignments need review before this form can be saved.");
    }
    const options = sites.filter((site) => site.isActive === true ||
      selected.includes(site.id)).map((site) => ({value: site.id,
      label: `${site.siteName}${site.isActive ? "" : " (archived)"}`,
      disabled: site.isActive !== true}));
    const defaultControl = createSelectControl({
      root: root.querySelector("[data-user-default-site-control]"),
      name: "defaultSiteId", accessibleName: "Default site", required: true,
      value: source.defaultSiteId || businessDefaultSiteId || "",
      options: options.filter((option) => selected.includes(option.value)),
      readOnly: source.isActive === false,
    });
    createSelectControl({root: root.querySelector("[data-user-sites-control]"),
      name: "siteIds", accessibleName: "Sites", required: true,
      value: selected, options, multiple: true, checkboxMenu: true,
      allSelectedLabel: options.length === 1 ? options[0].label : "All sites",
      selectedCountLabel: "Sites", emptySelectionLabel: "Select sites",
      readOnly: source.isActive === false,
    });
    root.querySelector("[data-user-sites-control]").addEventListener(
      "control:valuechange", (event) => {
        const ids = event.detail?.value || [];
        defaultControl.update({options: options.filter((option) =>
          ids.includes(option.value))});
        if (!ids.includes(defaultControl.value)) {
          defaultControl.setValue("");
        }
      },
    );
    form.dataset.siteFieldsReady = "true";
    status.textContent = source.role === "admin" ?
      "Admins retain access to all sites. Select the default used for their work." :
      "The default site must be included in the selected sites.";
  } catch (error) {
    status.textContent = error.message || "Site options could not be loaded. Reopen this form.";
    status.classList.add("text-red-600");
  }
}

/**
 * Refuses submission before site controls have loaded successfully.
 * @param {HTMLFormElement} form Source form.
 * @returns {void}
 */
export function requireUserSiteFieldsReady(form) {
  if (!areUserSiteFieldsAvailable()) {return;}
  if (form.dataset.siteFieldsReady !== "true") {
    throw new Error("Site options are unavailable. Wait or reopen this form.");
  }
}
